import { UrlService } from '@/common/url/url.service';
import { GtmPageView } from '@/core/domain/gtm/models/gtm-page-view.models';
import { SetDataLayerParamsUseCase } from '@/core/domain/gtm/usecases/setDataLayer.usecase';
import { PurchasePersistenceModel } from '@/core/domain/purchase-persistence/models/PurchasePersistence.model';
import { DeletePurchaseUseCase } from '@/core/domain/purchase-persistence/usecases/delete-purchase-persistence.usecase ';
import { GetPurchaseUseCase } from '@/core/domain/purchase-persistence/usecases/get-purchase-persistence.usecase';
import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, of, switchMap } from 'rxjs';
import { setProgressAction } from '../../../../data/current-progress/store/current-progress.actions';
import { PROGRESS_NAME } from '../../../../ui/shared/ui/vg-progress/vg-progress.component';

import { getLanguage } from '@/common/utils/strings/path.utils';
import { CheckLoginUseCase } from '@/core/domain/auth/usecases/check-login.usecase';
import { GtmPurchase } from '@/core/domain/gtm/models/gtm-purchase.model';
import { selectAuthValue } from '@/data/auth/store/auth.selectors';
import { DateTime } from 'luxon';

@Component({
    selector: 'vg-confirmation',
    templateUrl: './confirmation.component.html',
    styleUrls: ['./confirmation.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ConfirmationComponent implements OnDestroy {
    public purchasePersistence: PurchasePersistenceModel | null = null;
    public card$ = new BehaviorSubject({
        image: '',
        recipient: '',
        id: '',
        code: '',
        message: '',
        recipientEmail: '',
        deliveryDate: '',
        expirationDate: '',
        status: '',
        amount: 0,
        orderId: '',
        cardLastFour: '',
        purchaseName: '',
        purchaseAddress: '',
        gifter: '',
        title: '',
    });

    id = '';
    orderId = '';
    cardLastFour = '';
    purchaseName = '';
    purchaseAddress = '';
    status = '';
    code = '';

    public isUserLogged = false;

    constructor(
        // eslint-disable-next-line @ngrx/no-typed-global-store
        private readonly store: Store<{ currentProgress: PROGRESS_NAME }>,
        private readonly getPurchasePersistence: GetPurchaseUseCase,
        private readonly router: Router,
        private readonly url: UrlService,
        private readonly deletePurchaseCustomUseCase: DeletePurchaseUseCase,
        private readonly gtmDataLayer: SetDataLayerParamsUseCase,
        private readonly authCheckLogin: CheckLoginUseCase,
    ) {
        this.store.dispatch(setProgressAction({ value: 'confirmation' }));
        this.getCardInfo();

        this.store.select(selectAuthValue).subscribe(() => {
            this.initCheckLogin();
        });
    }

    ngOnDestroy(): void {
        this.deletePurchaseCustomUseCase.execute().subscribe();
    }

    private initCheckLogin() {
        this.authCheckLogin
            .execute()
            .pipe(
                switchMap((auth) => {
                    if (auth) {
                        this.isUserLogged = auth.isLogged;
                    }
                    return of(null);
                }),
            )
            .subscribe();
    }

    public getCardInfo(): void {
        const purchaseInfo = this.router.getCurrentNavigation()?.extras.state;
        if (purchaseInfo) {
            this.id = purchaseInfo['card'].cards[0].id;
            this.code = purchaseInfo['card'].cards[0].code;
            this.orderId = purchaseInfo['card'].purchaseInfo.invoiceId;
            this.status = purchaseInfo['card'].cards[0].status;
            this.cardLastFour = purchaseInfo['card'].purchaseInfo.cardLastFour;
            this.purchaseName = purchaseInfo['card'].purchaseInfo.name;
            this.purchaseAddress = purchaseInfo['card'].purchaseInfo.address;
        } else {
            this.url
                .format('dashboard/orders')
                .subscribe((url) => this.router.navigateByUrl(url));
        }

        this.getPurchasePersistence
            .execute()
            .subscribe((purchasePersistence) => {
                this.purchasePersistence = purchasePersistence;
                const language = getLanguage();

                this.card$.next({
                    image: this.purchasePersistence.cardDesign?.resource
                        ? this.purchasePersistence.cardDesign?.resource
                        : '',
                    recipient: this.purchasePersistence.shipment.recipientName,
                    id: this.id,
                    code: this.code,
                    message: this.purchasePersistence.message.message,
                    recipientEmail:
                        this.purchasePersistence.shipment.recipientEmail,
                    deliveryDate:
                        this.purchasePersistence.shipment
                            .recipientScheduleShipping,
                    expirationDate: this.addOneYearToDateTime(
                        this.purchasePersistence.shipment
                            .recipientScheduleShipping,
                    ),
                    status: this.status,
                    amount: this.purchasePersistence.amount,
                    orderId: this.orderId,
                    cardLastFour: this.cardLastFour,
                    purchaseName: this.purchaseName,
                    gifter: this.purchasePersistence.message.from,
                    purchaseAddress: this.purchaseAddress,
                    title: this.purchasePersistence.cardDesign!.title[language],
                });

                this.gtmAction();
            });
    }

    gtmPageView() {
        this.gtmDataLayer.execute<GtmPageView>({
            event: 'ev_pageview',
            page_path: this.router.url,
            page_title: 'confirmation',
        });
    }

    gtmAction() {
        this.gtmDataLayer.execute<GtmPurchase>({
            event: 'purchase',
            currency: 'MXN',
            value: this.card$.value.amount.toString(),
            coupon: '-',
            transaction_id: this.orderId,
            expiration_date: DateTime.fromISO(
                this.card$.value.expirationDate,
            ).toFormat('dd/MM/yyyy'),
            affiliation: 'viva_gift',
            tax: 0,
            shipping: 0,
            ecommerce: {
                items: [
                    {
                        item_id: this.card$.value.id,
                        item_name: 'Viva Gift',
                        currency: 'MXN',
                        discount: '0',
                        index: '1',
                        item_brand: 'VG',
                        item_category: 'Other',
                        item_list_id: 'Viva Gift',
                        item_list_name: 'Viva Gift',
                        item_variant: this.card$.value.title,
                        price: this.card$.value.amount.toString(),
                        quantity: '1',
                    },
                ],
            },
        });
    }

    addOneYearToDateTime(dateTimeString: string): string {
        const dateTime = new Date(dateTimeString);
        if (isNaN(dateTime.getTime())) {
            throw new Error('Invalid date time format');
        }
        dateTime.setFullYear(dateTime.getFullYear() + 1);
        return dateTime.toISOString();
    }
}
